import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { TcpSocket, DataEncoding } from 'capacitor-tcp-socket';
import { generateEscPos, stringToBase64 } from '@/lib/escpos';
import { useToast } from '@/hooks/use-toast';

interface NativeRelayProps {
    restaurantId?: string;
}

export function NativeRelay({ restaurantId }: NativeRelayProps) {
    const { toast } = useToast();
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<Number | null>(null);

    useEffect(() => {
        // Only run on native platforms (Android/iOS)
        if (!Capacitor.isNativePlatform()) {
            console.log('[NativeRelay] Not a native platform, skipping initialization');
            return;
        }

        if (!restaurantId) {
            console.log('[NativeRelay] No restaurantId provided, waiting...');
            return;
        }

        const connect = () => {
            // Configuration from localStorage managed by the dashboard
            const PRINTER_IP = localStorage.getItem('waiterix_printer_ip') || '192.168.1.100';
            const PRINTER_PORT = parseInt(localStorage.getItem('waiterix_printer_port') || '9100');

            // Get the correct WebSocket URL (use the same origin as the API)
            const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || window.location.origin;
            const wsUrl = apiBaseUrl.replace('http', 'ws') + '/ws';

            const url = `${wsUrl}?restaurantId=${restaurantId}&role=printer`;
            console.log(`[NativeRelay] Connecting to Waiterix Cloud: ${url}`);

            const ws = new WebSocket(url);
            socketRef.current = ws;

            ws.onopen = () => {
                console.log('[NativeRelay] Connected to cloud bridge');
                // Request any jobs created while we were offline
                ws.send(JSON.stringify({ type: 'get-pending-jobs' }));
            };

            ws.onmessage = async (event) => {
                try {
                    const message = JSON.parse(event.data);
                    console.log(`[NativeRelay] Received message: ${message.type}`);

                    switch (message.type) {
                        case 'new-print-job':
                            await handlePrintJob(message.data, PRINTER_IP, PRINTER_PORT);
                            break;
                        case 'pending-jobs':
                            console.log(`[NativeRelay] Found ${message.data.length} pending jobs`);
                            for (const job of message.data) {
                                await handlePrintJob(job, PRINTER_IP, PRINTER_PORT);
                            }
                            break;
                        case 'pong':
                            // Heartbeat received
                            break;
                    }
                } catch (error) {
                    console.error('[NativeRelay] Failed to process message:', error);
                }
            };

            ws.onclose = () => {
                console.log('[NativeRelay] Disconnected from cloud bridge');
                // Clean up and reconnect
                socketRef.current = null;
                reconnectTimeoutRef.current = window.setTimeout(connect, 5000);
            };

            ws.onerror = (error) => {
                console.error('[NativeRelay] WebSocket Error:', error);
            };
        };

        const handlePrintJob = async (job: any, ip: string, port: number) => {
            console.log(`[NativeRelay] Processing Job: ${job.id}`);

            try {
                const payload = JSON.parse(job.payload);
                const escposData = generateEscPos(payload);
                const base64Data = stringToBase64(escposData);

                await sendToPrinter(base64Data, ip, port);

                // Notify backend that job is completed
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    socketRef.current.send(JSON.stringify({
                        type: 'update-job-status',
                        jobId: job.id,
                        status: 'completed'
                    }));
                }

                console.log(`[NativeRelay] Job ${job.id} printed successfully`);
            } catch (error: any) {
                console.error(`[NativeRelay] Failed to process job ${job.id}:`, error);

                // Notify backend about the failure
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                    socketRef.current.send(JSON.stringify({
                        type: 'update-job-status',
                        jobId: job.id,
                        status: 'failed'
                    }));
                }

                toast({
                    title: "Print Error",
                    description: `Failed to print job: ${error.message || 'Unknown error'}`,
                    variant: "destructive"
                });
            }
        };

        const sendToPrinter = async (base64Data: string, ip: string, port: number) => {
            console.log(`[NativeRelay] Sending to printer at ${ip}:${port}`);

            const connectResult = await TcpSocket.connect({
                ipAddress: ip,
                port: port
            });

            try {
                await TcpSocket.send({
                    client: connectResult.client,
                    data: base64Data,
                    encoding: DataEncoding.BASE64
                });
                console.log('[NativeRelay] TCP data sent');
            } finally {
                await TcpSocket.disconnect({
                    client: connectResult.client
                });
                console.log('[NativeRelay] TCP socket closed');
            }
        };

        connect();

        // Heartbeat
        const heartbeatInterval = setInterval(() => {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000);

        return () => {
            clearInterval(heartbeatInterval);
            if (reconnectTimeoutRef.current) {
                window.clearTimeout(reconnectTimeoutRef.current as number);
            }
            if (socketRef.current) {
                socketRef.current.onclose = null; // Prevent reconnect on unmount
                socketRef.current.close();
            }
        };
    }, [restaurantId, toast]);

    return null; // This component has no UI
}
